import { useEffect, useState } from 'react';
import { Plug, Copy, Check, KeyRound } from 'lucide-react';
import { useToast } from '@/components/common/Toast';
import magnetService from '@/services/api/magnetService';
import { provisionMagnet, type ProvisionResult } from '../services/magnetPortalService';

function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <code className="flex-1 text-xs bg-muted/50 border border-border rounded-lg px-3 py-2 overflow-x-auto whitespace-nowrap">
          {value}
        </code>
        <button
          onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
          className="shrink-0 p-2 rounded-lg border border-border hover:bg-muted transition-colors"
          aria-label={`Copy ${label}`}
        >
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );
}

export default function ProvisionPanel() {
  const { addToast } = useToast();
  const [magnets, setMagnets] = useState<{ id: string; title: string }[]>([]);
  const [magnetId, setMagnetId] = useState('');
  const [slug, setSlug] = useState('');
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ProvisionResult | null>(null);

  useEffect(() => {
    magnetService.getAllMagnets()
      .then((res) => setMagnets(res.data.map((m) => ({ id: m.id, title: m.title }))))
      .catch(() => addToast({ type: 'error', title: 'Could not load magnets' }));
  }, []);

  const connect = async () => {
    if (!magnetId) { addToast({ type: 'error', title: 'Pick a magnet first' }); return; }
    setLoading(true);
    try {
      const res = await provisionMagnet(magnetId, { slug: slug || undefined, url: url || undefined });
      setResult(res);
      addToast({ type: 'success', title: 'Magnet connected — secret generated' });
    } catch (err: any) {
      addToast({ type: 'error', title: err.response?.data?.error || 'Provisioning failed' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="rounded-xl border border-border bg-card p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Plug className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">Connect a magnet to resources.cantier.com</h3>
        </div>

        <label className="block">
          <span className="text-xs text-muted-foreground">Magnet</span>
          <select
            value={magnetId}
            onChange={(e) => setMagnetId(e.target.value)}
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="">Select a magnet…</option>
            {magnets.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
          </select>
        </label>

        <label className="block">
          <span className="text-xs text-muted-foreground">Page slug (optional)</span>
          <input
            value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="/mes-maturity-guide"
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
          />
        </label>

        <label className="block">
          <span className="text-xs text-muted-foreground">Full page URL (optional)</span>
          <input
            value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://resources.cantier.com/mes-maturity-guide"
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
          />
        </label>

        <button
          onClick={connect} disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
        >
          <KeyRound className="h-4 w-4" />
          {loading ? 'Connecting…' : 'Generate secret & connect'}
        </button>
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="text-sm font-semibold mb-4">Connector config</h3>
        {!result ? (
          <p className="text-sm text-muted-foreground">
            Connect a magnet to generate its webhook secret and the values to paste into the
            <strong> Cantier Connector</strong> WordPress plugin.
          </p>
        ) : (
          <div className="space-y-3">
            <CopyRow label="Endpoint URL" value={result.endpoint} />
            <CopyRow label="Magnet ID" value={result.magnet.id} />
            <CopyRow label="Webhook secret (per-magnet)" value={result.webhookSecret} />
            <CopyRow
              label="Personalized link format"
              value={`${result.magnet.subsiteUrl || 'https://resources.cantier.com/<slug>'}?t=<MagnetDelivery.id>`}
            />
            <div className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
              The site-wide token in the plugin (matching <code>MAGNET_PORTAL_SECRET</code>) is the simplest setup.
              The per-magnet secret above is only needed if you prefer signed per-magnet webhooks.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
